import api from '@/services/api';
import { catalogueServiceId } from '../utils/pricedLines';
import { parseWireMoney } from '../utils/decimalMoney';
import { wireDueDate } from '../utils/basketDates';
import { isDateKey } from '../utils/dateKey';
import type {
    BasketPricingInput,
    MoneyPair,
    PricedBasket,
    PricedItemFigures,
    PricedLineFigures,
} from '../types/basketPricing.types';

type Dated = { due_date?: string };
type AddOnLine = { instance_id: number; add_on_id: string } & Dated;
type AnchorField = { anchor_date?: string };

export type BasketPricingRequestBody = AnchorField &
    (
        | { plan_id: string | null; services: ({ instance_id: string; service_id: string } & Dated)[]; add_ons: AddOnLine[] }
        | { service_plan_session_id: string; services: ({ instance_id: string } & Dated)[]; add_ons: AddOnLine[] }
    );

const withDueDate = <T extends object>(line: T, startDate: string | Date | null | undefined): T & Dated => {
    const dueDate = wireDueDate(startDate);
    return dueDate === null ? line : { ...line, due_date: dueDate };
};

/** A SMRiQ basket is keyed by the session id rather than the product type, so the
 *  request shape and the request key cannot disagree. */
export const toBasketPricingRequest = (input: BasketPricingInput): BasketPricingRequestBody => {
    const add_ons: AddOnLine[] = input.addOns.map((addOn) =>
        withDueDate({ instance_id: addOn.instanceId, add_on_id: addOn.addOnId }, addOn.startDate),
    );
    const anchor: AnchorField = input.anchorDate && isDateKey(input.anchorDate) ? { anchor_date: input.anchorDate } : {};

    if (input.servicePlanSessionId !== null) {
        return {
            service_plan_session_id: input.servicePlanSessionId,
            services: input.services.map((service) =>
                withDueDate({ instance_id: service.instanceId }, service.startDate),
            ),
            add_ons,
            ...anchor,
        };
    }

    return {
        plan_id: input.planId,
        services: input.services.map((service) =>
            withDueDate(
                { instance_id: service.instanceId, service_id: catalogueServiceId(service.instanceId) },
                service.startDate,
            ),
        ),
        add_ons,
        ...anchor,
    };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const malformed = (): never => {
    throw new Error('Malformed basket pricing response.');
};

const toItem = (raw: unknown): PricedItemFigures => {
    if (!isRecord(raw)) return malformed();
    const net = parseWireMoney(raw.net);
    const gross = parseWireMoney(raw.gross);
    if (typeof raw.item_id !== 'string' || net === null || gross === null) return malformed();
    return { itemId: raw.item_id, net, gross };
};

const toItems = (raw: unknown): readonly PricedItemFigures[] | undefined => {
    if (raw === undefined) return undefined;
    if (!Array.isArray(raw)) return malformed();
    return raw.map(toItem);
};

const toLine = (raw: unknown): PricedLineFigures => {
    if (!isRecord(raw)) return malformed();
    const { instance_id, unpriced } = raw;
    const net = parseWireMoney(raw.net);
    const gross = parseWireMoney(raw.gross);
    if (
        (typeof instance_id !== 'string' && typeof instance_id !== 'number') ||
        net === null ||
        gross === null ||
        typeof unpriced !== 'boolean'
    ) {
        return malformed();
    }
    const items = toItems(raw.items);
    return items === undefined
        ? { instanceId: instance_id, net, gross, unpriced }
        : { instanceId: instance_id, net, gross, unpriced, items };
};

const toMoneyPair = (raw: unknown): MoneyPair => {
    if (!isRecord(raw)) return malformed();
    const net = parseWireMoney(raw.net);
    const gross = parseWireMoney(raw.gross);
    if (net === null || gross === null) return malformed();
    return { net, gross };
};

const toTotals = (raw: unknown): PricedBasket['totals'] => {
    if (!isRecord(raw)) return malformed();
    const net = parseWireMoney(raw.net);
    const vat = parseWireMoney(raw.vat);
    const gross = parseWireMoney(raw.gross);
    if (net === null || vat === null || gross === null) return malformed();
    return { net, vat, gross };
};

/** The single reader of the smr basket pricing shape (also reused by Slice 3b for the
 *  `pricing` block of a `price_changed` 422). Throws rather than yielding zeros. */
export const normalisePricedBasket = (raw: unknown): PricedBasket => {
    if (!isRecord(raw)) return malformed();

    const lines = raw.lines;
    if (!isRecord(lines) || !Array.isArray(lines.services) || !Array.isArray(lines.add_ons)) {
        return malformed();
    }

    const subtotals = raw.subtotals;
    if (!isRecord(subtotals)) return malformed();

    if (typeof raw.pricing_version !== 'string') {
        return malformed();
    }

    return {
        services: lines.services.map(toLine),
        addOns: lines.add_ons.map(toLine),
        subtotals: {
            services: toMoneyPair(subtotals.services),
            addOns: toMoneyPair(subtotals.add_ons),
        },
        fees: toMoneyPair(raw.fees),
        totals: toTotals(raw.totals),
        pricingVersion: raw.pricing_version,
    };
};

/** POSTs the configured basket to smr-service and normalises the response. Components must
 *  not call this directly — `useBasketPricing`/`useQuoteBasketPricing` are the supported callers. */
export const fetchBasketPricing = async (
    input: BasketPricingInput,
    options: { suppressGlobalForbiddenToast?: boolean } = {},
): Promise<PricedBasket> => {
    const response = await api.post<{ data: unknown }>(
        `/smr/products/${input.productId}/price`,
        toBasketPricingRequest(input),
        options,
    );
    return normalisePricedBasket(response.data.data);
};
